import {
  CommonEntityActiveSwitch,
  CommonMultiDeleteModel,
  EntityCommonInfoModel,
  FilteredPagedModel,
  FilteredPagedQueryResult,
} from "../../entities/common";
import { ManualScoringModel, ScoringConfigModel, ScoringLogModel } from "../../entities/scoring";
import { getQuerySearchParams } from "../../utils";
import { identitySlice, TagType } from "./identityApi";

export const scoringApi = identitySlice.injectEndpoints({
  endpoints: (builder) => ({
    // ScoringConfig

    getScoringConfigCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/scoring/config/commonInfo",
      providesTags: [{ type: TagType.ScoringConfig, id: "LIST" }],
    }),
    getScoringConfigsFilteredPaged: builder.query<FilteredPagedQueryResult<ScoringConfigModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/scoring/config/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagType.ScoringConfig, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }) => ({
                type: TagType.ScoringConfig,
                id,
              })),
            ]
          : [{ type: TagType.ScoringConfig, id: "PARTIAL-LIST" }],
    }),
    getScoringConfig: builder.query<ScoringConfigModel, number>({
      query: (configId) => `/scoring/config/${configId}`,
      providesTags: (result, error, arg) => [{ type: TagType.ScoringConfig, id: arg }],
    }),
    addScoringConfig: builder.mutation({
      query: (model: ScoringConfigModel) => ({
        url: "/scoring/config",
        method: "POST",
        body: model,
      }),
      invalidatesTags: [
        { type: TagType.ScoringConfig, id: "LIST" },
        { type: TagType.ScoringConfig, id: "PARTIAL-LIST" },
      ],
    }),
    editScoringConfig: builder.mutation({
      query: (model: ScoringConfigModel) => ({
        url: `/scoring/config/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagType.ScoringConfig, id: arg.id }],
    }),
    switchActiveScoringConfig: builder.mutation({
      query: (model: CommonEntityActiveSwitch) => ({
        url: `/scoring/config/switchActive/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagType.ScoringConfig, id: arg.id }],
    }),
    deleteScoringConfig: builder.mutation({
      query: (configId: string) => ({
        url: `/scoring/config/${configId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagType.ScoringConfig, id: arg },
              { type: TagType.ScoringConfig, id: "LIST" },
            ],
    }),
    deleteMultiScoringConfigs: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/scoring/config/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id) => ({
                type: TagType.ScoringConfig,
                id,
              })),
              { type: TagType.ScoringConfig, id: "LIST" },
            ],
    }),

    // ScoringLog

    getScoringLogCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/scoring/log/commonInfo",
      providesTags: [{ type: TagType.ScoringLog, id: "LIST" }],
    }),
    getScoringLogsFilteredPaged: builder.query<FilteredPagedQueryResult<ScoringLogModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/scoring/log/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagType.ScoringLog, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }) => ({
                type: TagType.ScoringLog,
                id,
              })),
            ]
          : [{ type: TagType.ScoringLog, id: "PARTIAL-LIST" }],
    }),
    getScoringLog: builder.query<ScoringLogModel, number>({
      query: (logId) => `/scoring/log/${logId}`,
      providesTags: (result, error, arg) => [{ type: TagType.ScoringLog, id: arg }],
    }),
    manualScoring: builder.mutation<number, ManualScoringModel>({
      query: (model) => ({
        url: "/scoring/manual-scoring",
        method: "POST",
        body: model,
      }),
      invalidatesTags: [
        { type: TagType.ScoringLog, id: "LIST" },
        { type: TagType.ScoringLog, id: "PARTIAL-LIST" },
        { type: TagType.User, id: "LIST" },
        { type: TagType.User, id: "PARTIAL-LIST" },
      ],
    }),
  }),
});

export const {
  useGetScoringConfigCommonInfoQuery,
  useGetScoringConfigsFilteredPagedQuery,
  useGetScoringConfigQuery,
  useAddScoringConfigMutation,
  useEditScoringConfigMutation,
  useSwitchActiveScoringConfigMutation,
  useDeleteScoringConfigMutation,
  useDeleteMultiScoringConfigsMutation,

  useGetScoringLogCommonInfoQuery,
  useGetScoringLogsFilteredPagedQuery,
  useGetScoringLogQuery,
  useManualScoringMutation,
} = scoringApi;
